/**
 * EngineMenuFit hook: keeps the model picker inside the space it opens into.
 *
 * The menu opens upwards from the composer. A fixed `100vh - 12rem` cap only
 * fits when the composer is at the bottom of the panel; on the onboarding
 * screen it sits mid-page, so a long list ran off the top instead of
 * scrolling. On opening, this caps the menu at the room above the button (or,
 * where narrow screens pin it to the bottom of the panel, the room above that)
 * and scrolls to the Effort row, then to the checked model if that left it out.
 *
 * The cap is a CSS variable set with `this.js()`, so a LiveView patch keeps it.
 */

// Gap left above the menu, and the least it shrinks to before it may overflow.
const MARGIN_PX = 16
const MIN_PX = 160

/**
 * The menu's height cap in px.
 * @param {object} geometry - Where the menu opens
 * @param {number} geometry.labelTop - The button's top, from the viewport's top
 * @param {number} geometry.viewportHeight - The panel's height
 * @param {?number} geometry.fixedBottom - The menu's `bottom` when it is pinned
 * @returns {number} The cap in px
 */
export function menuMaxHeight ({ labelTop, viewportHeight, fixedBottom }) {
  const room = fixedBottom == null ? labelTop : viewportHeight - fixedBottom
  return Math.max(MIN_PX, Math.floor(room - MARGIN_PX))
}

// Scrolls `row` into the menu's view, if it isn't, without moving any page or
// panel scroller around it (which `scrollIntoView` would). Layout offsets, not
// client rects: the menu is mid-way through its scale-in when this runs.
function reveal (menu, row) {
  const item = row?.closest('li')
  if (!item) { return }
  const top = item.offsetTop
  const bottom = top + item.offsetHeight
  if (bottom > menu.scrollTop + menu.clientHeight) { menu.scrollTop = bottom - menu.clientHeight }
  if (top < menu.scrollTop) { menu.scrollTop = top }
}

const EngineMenuFit = {
  mounted () {
    this.fit = () => {
      const label = this.el.querySelector('#engine-selector-label')
      const menu = this.el.querySelector('#engine-selector-dropdown')
      if (!label || !menu) { return null }

      const style = window.getComputedStyle(menu)
      const max = menuMaxHeight({
        labelTop: label.getBoundingClientRect().top,
        viewportHeight: window.innerHeight,
        fixedBottom: style.position === 'fixed' ? parseFloat(style.bottom) : null
      })
      this.js().setAttribute(this.el, 'style', `--engine-menu-max: ${max}px`)
      return menu
    }

    // Scrolls only as the menu opens: focus moving between its rows, or the
    // panel resizing, leaves where the user scrolled to alone.
    this.onFocusIn = (event) => {
      if (this.el.contains(event.relatedTarget)) { return }
      const menu = this.fit()
      if (!menu) { return }
      reveal(menu, menu.querySelector('#engine-effort-toggle'))
      reveal(menu, menu.querySelector('[aria-current="true"]'))
    }

    this.el.addEventListener('focusin', this.onFocusIn)
    window.addEventListener('resize', this.fit)
  },

  destroyed () {
    window.removeEventListener('resize', this.fit)
  }
}

export default EngineMenuFit
